import type { RepoPaperRow } from "@/features/repos/api/paperRepos";
import type { ClipboardDocument, DocumentContent } from "@/features/clipboard/api/clipboard";
import type { Dispatch, SetStateAction } from "react";
import { createContext, useContext } from "react";

/** The row currently inspected in the left Details panel (folder_path attached
 * by the page). Distinct from the preview: selecting a row only inspects it;
 * the right preview opens on an explicit "View". */
export type InspectedDoc = (ClipboardDocument & { folder_path?: string }) | null;

/**
 * Clipboard actions surfaced in the left sidebar. `ClipboardPage` registers
 * these (it owns the selection + folder path + upload handlers); the sidebar
 * renders them. Null when the current route isn't the clipboard.
 */
export interface ClipboardActions {
  /** Selected files + folders combined (drives the Start chat count/enable). */
  selectionCount: number;
  /** Selected documents only (drives the Delete affordance). */
  selectedDocCount: number;
  /** The current folder view has any folders/docs (drives Export). */
  hasContent: boolean;
  /** False when viewing a folder shared by someone else (read-only). */
  canModify: boolean;
  isExporting: boolean;
  onUploadFiles: () => void;
  onUploadFolder: () => void;
  /** Open the "new folder" dialog for the current location. */
  onCreateFolder: () => void;
  /** Import already-obtained File objects (e.g. from Google Drive) into the upload pipeline. */
  onImportFiles: (files: File[]) => void;
  onStartChat: () => void;
  onExport: () => void;
  onBulkDelete: () => void;
  /** Open the right preview pane for the given inspected doc. */
  onView: (doc: InspectedDoc) => void;
  /** Open the right preview pane directly on the doc's original file (raw view). */
  onViewOriginal: (doc: InspectedDoc) => void;
  /** Download an inspected doc's original file. */
  onDownload: (doc: InspectedDoc) => void;
}

export type TreeViewOutletContextValue = {
  selectedPaper: RepoPaperRow | null;
  setSelectedPaper: Dispatch<SetStateAction<RepoPaperRow | null>>;
  commitsRepoId: string | null;
  closeMobileTreeView: () => void;
  clipboardPreview: DocumentContent | null;
  setClipboardPreview: Dispatch<SetStateAction<DocumentContent | null>>;
  clipboardPreviewLoading: boolean;
  setClipboardPreviewLoading: Dispatch<SetStateAction<boolean>>;
  /** True while the right-side Clipboard preview is visibly open. */
  clipboardPreviewOpen: boolean;
  clipboardActions: ClipboardActions | null;
  setClipboardActions: Dispatch<SetStateAction<ClipboardActions | null>>;
  inspectedDoc: InspectedDoc;
  setInspectedDoc: Dispatch<SetStateAction<InspectedDoc>>;
  /** Right-hand detail/preview panel state, rendered by TreeViewShell. */
  detailPanelOpen: boolean;
  panelWidth: number;
  setPanelWidth: Dispatch<SetStateAction<number>>;
  closeDetailPanel: () => void;
  /** Clear everything the right panel shows (used when leaving the shell). */
  resetPanels: () => void;
};

const defaultContext: TreeViewOutletContextValue = {
  selectedPaper: null,
  setSelectedPaper: () => { },
  commitsRepoId: null,
  closeMobileTreeView: () => { },
  clipboardPreview: null,
  setClipboardPreview: () => { },
  clipboardPreviewLoading: false,
  setClipboardPreviewLoading: () => { },
  clipboardPreviewOpen: false,
  clipboardActions: null,
  setClipboardActions: () => { },
  inspectedDoc: null,
  setInspectedDoc: () => { },
  detailPanelOpen: false,
  panelWidth: 384,
  setPanelWidth: () => { },
  closeDetailPanel: () => { },
  resetPanels: () => { },
};

export const TreeViewOutletContext = createContext<TreeViewOutletContextValue>(defaultContext);

export const useTreeViewOutletContext = (): TreeViewOutletContextValue => {
  return useContext(TreeViewOutletContext);
}
